/// <reference types="vite/client" />

/**
 * Application-level constants
 */

// Base paths
export const BASE_PATHS = {
  API: '/api',
  ASSETS: '/assets',
  IMAGES: '/images',
  APP: '/',
  MFE_BASE: '/loan_officer_app_ui/', // For MFE compatibility
} as const;

/**
 * Gets the asset path with proper base URL prefix for Vite builds
 * Vite automatically provides import.meta.env.BASE_URL based on the base config
 * @param assetPath - Path to asset (e.g., '/assets/icons/pdf_icon.png')
 * @returns Asset path with base URL prefix (e.g., '/loan_officer_app_ui/assets/icons/pdf_icon.png')
 */
export const getAssetPath = (assetPath: string): string => {
  const baseUrl = import.meta.env.BASE_URL;
  // Ensure baseUrl ends with /
  const normalizedBase = baseUrl.endsWith('/') ? baseUrl : baseUrl + '/';
  // Remove leading slash from assetPath if present, then combine with baseUrl
  const cleanPath = assetPath.startsWith('/') ? assetPath.slice(1) : assetPath;
  // Ensure baseUrl ends with / and path doesn't start with /
  return `${normalizedBase}${cleanPath}`;
};

// Route paths
export const ROUTES = {
  HOME: '/',
  DASHBOARD: '/dashboard',
  LOANS: '/loans',
  LOAN_DETAIL: '/loans/:id',
  SETTINGS: '/settings',
  PROFILE: '/profile',
  NOT_FOUND: '/404',
} as const;

// API endpoints
export const API_ENDPOINTS = {
  DASHBOARD: '/dashboard',
  LOANS: '/loans',
  LOAN_BY_ID: (id: string) => `/loans/${id}`,
  CONFIG: '/config',
  FEATURE_FLAGS: '/feature-flags',
  PROFILE: '/profile',
  AUTH: {
    LOGIN: '/auth/login',
    LOGOUT: '/auth/logout',
    REFRESH: '/auth/refresh',
  },
  LOAN_OFFICERS: {
    BASE: '',
    BY_ID: (id: string) => `/${id}`,
    BY_IDENTIFIER: '/identifier',
  },
  BRANCHES: {
    BASE: '',
    BY_ID: (id: string) => `/${id}`,
  },
} as const;

// Storage keys
export const STORAGE_KEYS = {
  AUTH_TOKEN: 'auth_token',
  REFRESH_TOKEN: 'refresh_token',
  USER_PREFERENCES: 'user_preferences',
  LANGUAGE: 'language',
} as const;

// UI constants
export const UI = {
  SIDEBAR_WIDTH: 240,
  HEADER_HEIGHT: 64,
  DRAWER_WIDTH: 360,
  MAX_CONTENT_WIDTH: 1440,
  DEBOUNCE_DELAY: 300,
  TOAST_DURATION: 3000,
} as const;

// Pagination
export const PAGINATION = {
  DEFAULT_PAGE: 1,
  DEFAULT_PAGE_SIZE: 10,
  PAGE_SIZE_OPTIONS: [10, 25, 50, 100],
} as const;

// Date formats
export const DATE_FORMATS = {
  DISPLAY: 'MMM DD, YYYY',
  DISPLAY_WITH_TIME: 'MMM DD, YYYY HH:mm',
  API: 'YYYY-MM-DD',
  API_WITH_TIME: 'YYYY-MM-DDTHH:mm:ssZ',
} as const;

// Validation
export const VALIDATION = {
  MIN_PASSWORD_LENGTH: 8,
  MAX_PASSWORD_LENGTH: 128,
  MAX_FILE_SIZE_MB: 10,
  ALLOWED_FILE_TYPES: ['pdf', 'jpg', 'jpeg', 'png', 'doc', 'docx'],
} as const;

// Feature flags (local overrides)
export const FEATURE_FLAGS = {
  ENABLE_DARK_MODE: true,
  ENABLE_NOTIFICATIONS: true,
  ENABLE_EXPORT: true,
  ENABLE_BULK_ACTIONS: false,
} as const;

// App metadata
export const APP_META = {
  TITLE: 'POS Web App',
  DESCRIPTION: 'Modern point of sale application management system',
  AUTHOR: 'Your Organization',
  VERSION: '0.0.1',
} as const;
